import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  AccountTypeValues,
  BusinessRegistry,
  BusinessRegistryValues,
  ErrorCode,
  type BusinessRegistryResult,
  type LinkedinCompleteSignupDto,
  type ProblemDetails,
  type SignUpDto,
} from '@paladin-monorepo/common';

import { AuthSubmitButton } from '@/components/auth/AuthSubmitButton';
import {
  authFieldClassName,
  authLabelClassName,
  authLinkClassName,
} from '@/components/auth/authStyles';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/forms/PasswordInput';
import API from '@/lib/api';
import i18n from '@/i18n';
import { useAuth } from '@/lib/auth';
import { passwordMinLength } from '@/config/passwordMinLength';
import { cn } from '@/lib/utils';

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '@/components/ui/input-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { trackEvent } from '@/lib/analytics';
import { linkedinApi } from '@/lib/api/linkedin';
import { googleApi } from '@/lib/api/google';
import type { SsoSignupProvider } from '@/pages/auth/signup';
import { invalidateAllQueries } from '@/lib/queryCache';
import { useQueryClient } from '@tanstack/react-query';
import { userNeedsSupplierOnboarding } from '@/lib/supplierOnboarding';
import { userNeedsRegistrationGate } from '@/lib/registrationApproval';
import type { UserProps } from '@/lib/auth';
import { useBusinessRegistrySearch } from '@/hooks/useBusinessRegistrySearch';
import { isRegistryCompanyIdRequired, useRegistryLookupEnabled } from '@/hooks/useRegistryLookupEnabled';
import { RegistryCountrySelect } from '@/forms/SignupForm/RegistryCountrySelect';
import { StepComponent } from '@/components/StepNavigation';
import * as Sentry from '@sentry/react';

interface SignupFormProps {
  onSuccess: () => void;
}

export interface SignupFormValues {
  businessRegistry: BusinessRegistry;
  companyId: string;
  company: string;
  accountType: SignUpDto['accountType'];
  fullName: string;
  email: string;
  password: string;
  acceptedTerms: boolean;
}

export const CompanySignUpForm: React.ComponentType<
  StepComponent & { onSuccess: () => void }
> = ({
  onBack,
  onContinue: _onContinue,
  prerequisite: _prerequisite,
  onSuccess,
  state,
  setState,
  className,
  ...props
}: React.ComponentProps<'div'> & StepComponent & SignupFormProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const refParam = searchParams.get('ref');
  const ssoProvider: SsoSignupProvider | null = (() => {
    if (state?.ssoProvider) {
      return state.ssoProvider;
    }
    if (state?.linkedinPathActive || refParam === 'linkedin') {
      return 'linkedin';
    }
    if (state?.googlePathActive || refParam === 'google') {
      return 'google';
    }
    return null;
  })();
  const isSsoPath = ssoProvider !== null;

  const schema = useMemo(() => {
    const baseShape = {
      accountType: yup.string()
        .oneOf(AccountTypeValues, t('signup.form.accountType.errors.invalid'))
        .required(t('signup.form.accountType.errors.required')),
      businessRegistry: yup.mixed<BusinessRegistry>()
        .oneOf(BusinessRegistryValues as BusinessRegistry[])
        .required(),
      companyId: yup.string()
        .default('')
        .when('businessRegistry', {
          is: (value: BusinessRegistry) =>
            value !== BusinessRegistry.OTHER && value !== BusinessRegistry.PL,
          then: (fieldSchema) => fieldSchema.required(t('signup.form.company.errors.required')),
        }),
      company: yup.string()
        .required(t('signup.form.company.errors.required')),
      fullName: yup.string()
        .required(t('signup.form.fullName.errors.required')),
      email: yup.string()
        .email(t('signup.form.email.errors.invalid'))
        .required(t('signup.form.email.errors.required')),
      acceptedTerms: yup.boolean()
        .oneOf([true], t('signup.form.terms.errors.required'))
        .required(t('signup.form.terms.errors.required')),
    };

    if (isSsoPath) {
      return yup.object().shape({
        ...baseShape,
        password: yup.string().optional(),
      });
    }

    return yup.object().shape({
      ...baseShape,
      password: yup.string()
        .min(passwordMinLength, t('signup.form.password.errors.minLength', { minLength: passwordMinLength }))
        .required(t('signup.form.password.errors.required')),
    });
  }, [t, isSsoPath]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<BusinessRegistryResult | undefined>();
  const [companyCheckStatus, setCompanyCheckStatus] = useState<'idle' | 'pending' | 'ok' | 'error'>('idle');

  const methods = useForm<SignupFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      accountType: state?.accountType,
      businessRegistry: BusinessRegistry.CZ,
      companyId: '',
      company: '',
      fullName: '',
      email: '',
      password: '',
      acceptedTerms: false,
      ...state?.userDetails,
    },
  });

  const businessRegistry = methods.watch('businessRegistry');
  const { lookupEnabled: registryLookupEnabled } = useRegistryLookupEnabled(businessRegistry);
  /** Registry lookup is required when the backend has lookup configured for this country. */
  const isRegistryValidated = isRegistryCompanyIdRequired(businessRegistry, registryLookupEnabled);

  const onRegistrySearchError = useCallback(
    (error: unknown) => {
      const errorData = (error as { response?: { data?: ProblemDetails } }).response?.data;
      if (errorData?.code === ErrorCode.BadRequest) {
        methods.setError(
          'company',
          { message: t('signup.form.company.errors.invalid') },
          { shouldFocus: true },
        );
      }
    },
    [methods, t],
  );

  const {
    results: registryResults,
    setResults: setRegistryResults,
    isSearching,
    search,
    skipNextSearchRef,
  } = useBusinessRegistrySearch(businessRegistry, {
    debounceMs: 350,
    lookupEnabled: registryLookupEnabled,
    onSearchError: onRegistrySearchError,
    onSearchSuccess: () => methods.clearErrors('company'),
  });

  const onRegistryCountryChange = useCallback(
    (next: BusinessRegistry) => {
      methods.setValue('businessRegistry', next);
      methods.setValue('companyId', '');
      methods.clearErrors('company');
      setSelectedCompany(undefined);
      setRegistryResults([]);
      setCompanyCheckStatus('idle');
    },
    [methods, setRegistryResults],
  );

  const checkCompanyExists = useCallback(
    (ico: string, companyName: string) => {
      setCompanyCheckStatus('pending');
      API.get(`/auth/check-company/${ico}`)
        .then((res) => {
          if (res.data?.exists) {
            navigate('/auth/signup/company-exists', {
              state: {
                companyId: ico,
                companyName,
              },
            });
            return;
          }
          setCompanyCheckStatus('ok');
        })
        .catch((err) => {
          Sentry.captureException(err);
          setCompanyCheckStatus('error');
          toast.error(t('signup.form.company.errors.checkFailed'));
        });
    },
    [navigate, t],
  );

  const { csrf } = useAuth({
    middleware: 'guest',
    redirectTo: '/',
    tmp: 'SignupForm',
  });

  useEffect(() => {
    if (selectedCompany?.isNaturalPerson) {
      methods.setValue('fullName', selectedCompany.name);
    }
  }, [selectedCompany, methods]);

  useEffect(() => {
    if (state?.userDetails) {
      methods.reset({
        ...methods.getValues(),
        ...state.userDetails,
        accountType: state.accountType ?? state.userDetails.accountType,
      });
    }
  }, [state?.userDetails, state?.accountType, methods]);

  const buildCompanyPayload = (values: SignupFormValues, registryValidated: boolean) => {
    if (registryValidated && selectedCompany) {
      return {
        companyId: values.companyId,
        company: {
          name: selectedCompany.name,
          vatId: selectedCompany.vatId,
          address: {
            street: selectedCompany.address.street ?? '',
            city: selectedCompany.address.city ?? '',
            zip: selectedCompany.address.zip ?? '',
            country: selectedCompany.address.country ?? '',
          },
        },
      };
    }

    return {
      companyId: undefined,
      company: {
        name: values.company.trim(),
      },
    };
  };

  const onSubmit = async (values: SignupFormValues) => {
    if (isSubmitting) return;
    if (Object.keys(methods.formState.errors).length > 0) return;
    if (!values.email) return;
    if (!isSsoPath && !values.password) return;
    const registryValidated = isRegistryCompanyIdRequired(values.businessRegistry, registryLookupEnabled);
    if (registryValidated && !selectedCompany) {
      methods.setError('company', {
        message: t('signup.form.company.errors.notSelected'),
      });
      return;
    }

    setIsSubmitting(true);

    await csrf();

    if (isSsoPath && ssoProvider) {
      const companyPayload = buildCompanyPayload(values, registryValidated);
      const payload: LinkedinCompleteSignupDto = {
        businessRegistry: values.businessRegistry,
        ...companyPayload,
        accountType: values.accountType!,
        acceptedTerms: values.acceptedTerms,
      };

      const completeApi = ssoProvider === 'google' ? googleApi : linkedinApi;
      const pendingExpiredKey =
        ssoProvider === 'google'
          ? 'auth.google.errors.pendingExpired'
          : 'auth.linkedin.errors.pendingExpired';

      completeApi
        .completeSignup(payload)
        .then(async () => {
          await invalidateAllQueries(queryClient);
          const refreshed = queryClient.getQueryData<UserProps>(['/me']);
          if (userNeedsRegistrationGate(refreshed)) {
            navigate('/waiting-list');
          } else {
            navigate(
              userNeedsSupplierOnboarding(refreshed) ? '/onboarding' : '/monomo-ai',
            );
          }
        })
        .catch((error) => {
          if (error.response?.status === 401) {
            toast.error(t(pendingExpiredKey));
            return;
          }
          if (error.status === 409) {
            const errorType = error.response?.data?.type;
            if (errorType === 'https://developers.paladinhub.cz/errors/company-already-exists') {
              navigate('/auth/signup/company-exists', {
                state: {
                  companyId: values.companyId,
                  companyName: selectedCompany?.name ?? values.company,
                },
              });
            } else {
              toast.error(t('signup.errors.userAlreadyExists.title'), {
                description: t('signup.errors.userAlreadyExists.description'),
              });
            }
          } else {
            console.error(error);
            toast.error(t('signup.errors.default.title'), {
              description: t('signup.errors.default.description'),
            });
          }
        })
        .finally(() => {
          setIsSubmitting(false);
        });
      return;
    }

    API.post(
      '/auth/signup',
      {
        businessRegistry: values.businessRegistry,
        companyId: registryValidated ? values.companyId : undefined,
        company: registryValidated
          ? {
              name: selectedCompany!.name,
              vatId: selectedCompany!.vatId,
              address: {
                street: selectedCompany!.address.street ?? '',
                city: selectedCompany!.address.city ?? '',
                zip: selectedCompany!.address.zip ?? '',
                country: selectedCompany!.address.country ?? '',
              },
            }
          : {
              name: values.company.trim(),
            },
        accountType: values.accountType!,
        fullName: values.fullName,
        email: values.email,
        password: values.password,
        acceptedTerms: values.acceptedTerms,
        language: i18n.language?.startsWith('en') ? 'en' : 'cs',
      } satisfies SignUpDto,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )
      .then((response) => {
        if (response.status >= 200 && response.status < 300) {
          trackEvent('sign_up', { method: 'password' });
          return onSuccess();
        } else {
          toast.error(t('signup.errors.default.title'), {
            description: t('signup.errors.default.description'),
          });
        }
      })
      .catch((error) => {
        if (error.status === 409) {
          const errorType = error.response?.data?.type;
          if (errorType === 'https://developers.paladinhub.cz/errors/company-already-exists') {
            navigate('/auth/signup/company-exists', {
              state: {
                companyId: values.companyId,
                companyName: selectedCompany?.name ?? '',
              },
            });
          } else {
            toast.error(t('signup.errors.userAlreadyExists.title'), {
              description: t('signup.errors.userAlreadyExists.description'),
            });
          }
        } else {
          console.error(error);
          toast.error(t('signup.errors.default.title'), {
            description: t('signup.errors.default.description'),
          });
        }

        return error;
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <div className={cn('mx-auto flex w-full max-w-[480px] flex-col gap-6', className)} {...props}>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} className="w-full">
          <div className="flex flex-col gap-8">
            {selectedCompany && (
              <div className="flex flex-col gap-1">
                <Card className="border-border-subtle bg-brand-secondary-bright/5 p-2">
                  <CardContent className="px-2">
                    <span className="text-caption text-brand-secondary/70">
                      {selectedCompany.businessId}
                    </span>
                    <br />
                    {selectedCompany.name}
                    <br />
                    {selectedCompany.address.formatted}
                  </CardContent>
                </Card>
                <button
                  type="button"
                  className="self-start text-xs text-brand-secondary/70 underline underline-offset-2 transition-colors hover:text-brand-secondary"
                  onClick={() => {
                    setSelectedCompany(undefined);
                    methods.setValue('companyId', '');
                    methods.setValue('company', '');
                    setRegistryResults([]);
                    setCompanyCheckStatus('idle');
                  }}
                >
                  {t('signup.form.company.changeSelection', 'Změnit výběr')}
                </button>
              </div>
            )}

            {selectedCompany && companyCheckStatus === 'pending' && (
              <p className="text-sm text-brand-secondary/70">
                {t('signup.form.company.errors.checkPending')}
              </p>
            )}

            {selectedCompany && companyCheckStatus === 'error' && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => checkCompanyExists(selectedCompany.businessId, selectedCompany.name)}
              >
                {t('generic.retry')}
              </Button>
            )}

            <FormField
              control={methods.control}
              name="company"
              render={({ field }) => (
                <FormItem className={selectedCompany ? 'hidden' : undefined}>
                  <FormLabel className={authLabelClassName}>
                    {isRegistryValidated
                      ? t('signup.form.company.label')
                      : t('signup.form.company.labelOther')}
                  </FormLabel>
                      <FormControl>
                        {isRegistryValidated ? (
                          <Combobox<BusinessRegistryResult>
                            items={registryResults}
                            itemToStringValue={(item: BusinessRegistryResult) => item.businessId}
                            onValueChange={(value: BusinessRegistryResult | null) => {
                              if (value) {
                                skipNextSearchRef.current = true;
                                setSelectedCompany(value);
                                methods.setValue('companyId', value.businessId);
                                methods.setValue('company', value.name);
                                methods.clearErrors('company');
                                setRegistryResults([]);
                                checkCompanyExists(value.businessId, value.name);
                              }
                            }}
                            onInputValueChange={(inputValue: string) => {
                              if (inputValue !== '') {
                                methods.setValue('company', inputValue);
                                search(inputValue);
                              }
                            }}
                          >
                            <ComboboxInput
                              placeholder={
                                businessRegistry === BusinessRegistry.PL
                                  ? t('signup.form.company.placeholderPl')
                                  : t('signup.form.company.placeholder')
                              }
                              disabled={isSubmitting}
                              showTrigger={false}
                            >
                              <InputGroupAddon align="inline-end" className="cursor-default pr-1">
                                <RegistryCountrySelect
                                  value={businessRegistry}
                                  onChange={onRegistryCountryChange}
                                  disabled={isSubmitting}
                                />
                              </InputGroupAddon>
                            </ComboboxInput>
                            <ComboboxContent>
                              <ComboboxEmpty>
                                {isSearching
                                  ? t('signup.form.company.searching', 'Hledání...')
                                  : t('signup.form.company.noResults', 'Žádné výsledky')}
                              </ComboboxEmpty>
                              <ComboboxList>
                                {(_item) => {
                                  const item = _item as BusinessRegistryResult;
                                  return (
                                    <ComboboxItem key={item.businessId} value={item} className="flex-col items-start py-2">
                                      <span className="text-xs text-muted-foreground">{item.businessId}</span>
                                      <span className="font-medium">{item.name}</span>
                                      {item.address.formatted && (
                                        <span className="text-xs text-muted-foreground">{item.address.formatted}</span>
                                      )}
                                    </ComboboxItem>
                                  );
                                }}
                              </ComboboxList>
                            </ComboboxContent>
                          </Combobox>
                        ) : (
                          <InputGroup>
                            <InputGroupInput
                              placeholder={
                                businessRegistry === BusinessRegistry.PL
                                  ? t('signup.form.company.placeholderPl')
                                  : t('signup.form.company.placeholderOther')
                              }
                              disabled={isSubmitting}
                              value={field.value ?? ''}
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                              name={field.name}
                            />
                            <InputGroupAddon align="inline-end" className="cursor-default pr-1">
                              <RegistryCountrySelect
                                value={businessRegistry}
                                onChange={onRegistryCountryChange}
                                disabled={isSubmitting}
                              />
                            </InputGroupAddon>
                          </InputGroup>
                        )}
                      </FormControl>
                      {!isRegistryValidated && (
                        <p className="text-xs text-brand-secondary/70">
                          {t('signup.form.company.otherHint')}
                        </p>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={methods.control}
                  name="fullName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={authLabelClassName}>
                        {t('signup.form.fullName.label')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          className={authFieldClassName}
                          placeholder={t('signup.form.fullName.placeholder')}
                          disabled={isSubmitting}
                          autoComplete="name"
                          {...field}
                          value={field.value ?? ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={methods.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={authLabelClassName}>
                        {t('signup.form.email.label')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          className={authFieldClassName}
                          placeholder={t('signup.form.email.placeholder')}
                          disabled={isSubmitting}
                          autoComplete="email"
                          {...field}
                          value={field.value ?? ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {!isSsoPath && (
                  <FormField
                    control={methods.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className={authLabelClassName}>
                          {t('signup.form.password.label')}
                        </FormLabel>
                        <FormControl>
                          <PasswordInput
                            className={authFieldClassName}
                            placeholder={t('signup.form.password.placeholder')}
                            disabled={isSubmitting}
                            autoComplete="new-password"
                            minLength={passwordMinLength}
                            {...field}
                            value={field.value ?? ''}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                <FormField
                  control={methods.control}
                  name="acceptedTerms"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-start gap-3">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={(checked) => field.onChange(checked === true)}
                            disabled={isSubmitting}
                          />
                        </FormControl>
                        <FormLabel className="cursor-pointer text-sm font-normal leading-relaxed text-brand-secondary">
                          <span>
                            <Trans
                              i18nKey="signup.form.terms.label"
                              components={[
                                <a
                                  href="/legal/terms-of-service.pdf"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-brand-primary underline underline-offset-4 hover:text-brand-primary-dark"
                                />,
                                <a
                                  href="/legal/privacy-policy.pdf"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-brand-primary underline underline-offset-4 hover:text-brand-primary-dark"
                                />,
                              ]}
                            />
                          </span>
                        </FormLabel>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="col-span-2 flex flex-col gap-4 text-center text-sm">
                  <AuthSubmitButton
                    disabled={isSubmitting || companyCheckStatus === 'pending' || companyCheckStatus === 'error'}
                    type="submit"
                    tabIndex={2}
                  >
                    {isSubmitting
                      ? t('signup.form.submit.loading')
                      : t('signup.form.submit.label')}
                  </AuthSubmitButton>

                  <Button
                    variant="ghost"
                    className={authLinkClassName}
                    onClick={() => {
                      setState((state) => {
                        return {
                          ...state,
                          userDetails: methods.getValues(),
                        };
                      });
                      onBack!();
                    }}
                  >
                    {t('signup.steps.backToAccountType')}
                  </Button>
                </div>
              </div>
            </form>
          </FormProvider>
    </div>
  );
};
